import { create } from "zustand";

/**
 * EU Scope Exchange Fabric Store (v4)
 * 
 * Manages EU-wide scope exchange fabric, replication lag, reconciliation errors,
 * fabric health scoring, and escalation levels.
 */
export interface EUFabricStoreState {
  memberStates: string[];
  replicationLagMS: number;
  reconciliationErrors: number;
  fabricHealthScore: number; // 0-100
  interopVersion: string;
  crossBorderPackets: number;
  anomalyFlags: number;
  escalationLevel: "info" | "warning" | "critical";

  // Actions
  setMemberStates: (states: string[]) => void;
  setCrossBorderPackets: (packets: number) => void;
  setInteropVersion: (version: string) => void;

  // Actions
  simulateReplication: () => void;
  triggerEscalation: () => void;

  // Derived outputs
  getInteropPenalty: () => number;
  getBorderFriction: () => number;
  getEscalationIndex: () => number;
  getFabricStatus: () => number;

  // Export
  exportFabricEnvelope: () => void;
}

export const useEUFabricStore = create<EUFabricStoreState>((set, get) => ({
  memberStates: [],
  replicationLagMS: 0,
  reconciliationErrors: 0,
  fabricHealthScore: 100,
  interopVersion: "1.0.0",
  crossBorderPackets: 0,
  anomalyFlags: 0,
  escalationLevel: "info",

  setMemberStates: (states) => set({ memberStates: states }),
  setCrossBorderPackets: (packets) => set({ crossBorderPackets: Math.max(0, packets) }),
  setInteropVersion: (version) => set({ interopVersion: version }),
  setFabricHealthScore: (score) => set({ fabricHealthScore: Math.max(0, Math.min(100, score)) }),

  simulateReplication: () => {
    const state = get();
    const newLag = Math.floor(Math.random() * 451) + 50; // 50-500
    const newErrors = state.reconciliationErrors + Math.floor(Math.random() * 3); // 0-2
    const newAnomalies = state.anomalyFlags + Math.floor(Math.random() * 2); // 0-1

    set({
      replicationLagMS: newLag,
      reconciliationErrors: newErrors,
      anomalyFlags: newAnomalies,
    });

    // Auto-trigger escalation check
    get().triggerEscalation();
  },

  triggerEscalation: () => {
    const status = get().getFabricStatus();
    let newLevel: "info" | "warning" | "critical" = "info";

    if (status < 40) {
      newLevel = "critical";
    } else if (status < 60) {
      newLevel = "warning";
    }

    set({ escalationLevel: newLevel });
  },

  getInteropPenalty: () => {
    const state = get();
    return (100 - state.fabricHealthScore) * 0.3;
  },

  getBorderFriction: () => {
    return get().crossBorderPackets * 0.04;
  },

  getEscalationIndex: () => {
    const state = get();
    return state.anomalyFlags * 2 + state.reconciliationErrors * 5;
  },

  getFabricStatus: () => {
    const state = get();
    const interopPenalty = state.getInteropPenalty();
    const borderFriction = state.getBorderFriction();
    const escalationIndex = state.getEscalationIndex();
    const status = 100 - (interopPenalty + borderFriction + escalationIndex);
    return Math.max(0, Math.min(100, status));
  },

  exportFabricEnvelope: () => {
    const state = get();
    const report = {
      memberStates: state.memberStates,
      replicationLagMS: state.replicationLagMS,
      reconciliationErrors: state.reconciliationErrors,
      anomalyFlags: state.anomalyFlags,
      crossBorderPackets: state.crossBorderPackets,
      escalationLevel: state.escalationLevel,
      fabricStatus: state.getFabricStatus(),
      interopVersion: state.interopVersion,
      interopPenalty: state.getInteropPenalty(),
      borderFriction: state.getBorderFriction(),
      escalationIndex: state.getEscalationIndex(),
    };

    const dateStr = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fabricEnvelope_${dateStr}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },
}));

